import {
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import { productVariant } from "./catalog";
import { customer, customerAddress } from "./customer";
import { shippingCarrier } from "./shipping";
import { socialSalesSource } from "./social-sales-source";

export const orderStatusEnum = [
  "pending_payment",
  "confirmed",
  "packing",
  "shipped",
  "completed",
  "return_in_transit",
  "returned",
  "cancelled",
] as const;

/** เว็บ · ขายหน้าร้าน (POS) · ขายผ่านโซเชียล (สร้างจาก POS แต่ flow เหมือนเว็บ) */
export const orderChannelEnum = ["web", "retail", "social"] as const;

export const order = pgTable(
  "order",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    /** รหัสคำสั่งซื้อสำหรับแสดง/ค้นหา เช่น ORD-0001, ORD-10000 */
    code: text("code").notNull().unique(),
    customerId: text("customer_id").references(() => customer.id, {
      onDelete: "set null",
    }),
    status: text("status", { enum: orderStatusEnum })
      .notNull()
      .default("pending_payment"),
    paymentMethod: text("payment_method", {
      enum: ["cod", "online", "cash", "bank_transfer"],
    }),
    channel: text("channel", { enum: orderChannelEnum })
      .notNull()
      .default("web"),
    /** POS Social Media — FK เมื่อ channel = social */
    socialSalesSourceId: text("social_sales_source_id").references(
      () => socialSalesSource.id,
      { onDelete: "set null" },
    ),
    /** ยอดรวม (หน่วยเล็กสุด) — หลังหักส่วนลด รวมค่าส่ง */
    total: integer("total").notNull(),
    /** ส่วนลดท้ายบิล (หน่วยเล็กสุด) — ไม่เกินย่อยรวมสินค้า + ค่าส่ง */
    discountTotal: integer("discount_total").notNull().default(0),
    /** ค่าขนส่ง (หน่วยเล็กสุด); null = ยังไม่ได้คำนวณ */
    shippingFee: integer("shipping_fee"),
    /** อ้างอิงที่อยู่ที่บันทึก (สมาชิกเลือกที่อยู่) */
    shippingAddressId: text("shipping_address_id").references(
      () => customerAddress.id,
      { onDelete: "set null" },
    ),
    /** Snapshot ที่อยู่จัดส่ง (สำหรับแสดงใน order + รองรับ guest); null ได้สำหรับ order เก่า */
    shippingRecipientName: text("shipping_recipient_name"),
    shippingPhone: text("shipping_phone"),
    /** ບ້ານ (village) — snapshot */
    shippingVillage: text("shipping_village"),
    shippingProvinceId: text("shipping_province_id"),
    shippingDistrictId: text("shipping_district_id"),
    shippingZone: text("shipping_zone"),
    shippingCarrierId: text("shipping_carrier_id").references(
      () => shippingCarrier.id,
      { onDelete: "set null" },
    ),
    /** สาขาผู้ให้บริการ — snapshot */
    carrierBranch: text("carrier_branch"),
    trackingNumber: text("tracking_number"),
    /** HAL pre-order/shipment mapping. trackingNumber remains the carrier-visible fallback. */
    halPreOrderId: integer("hal_pre_order_id"),
    halShipmentNumber: text("hal_shipment_number"),
    halShipmentStatusId: integer("hal_shipment_status_id"),
    halDeliveryStateId: integer("hal_delivery_state_id"),
    halMetadata: jsonb("hal_metadata").$type<Record<string, unknown>>(),
    halSyncedAt: timestamp("hal_synced_at", {
      mode: "string",
      withTimezone: true,
    }),
    /** ตัดสต็อกตอนเข้า confirmed แล้ว (idempotency / audit) */
    stockDeductedAt: timestamp("stock_deducted_at", {
      mode: "string",
      withTimezone: true,
    }),
    /** คืนสต็อกเมื่อยกเลิกจาก confirmed แล้ว */
    stockRestoredAt: timestamp("stock_restored_at", {
      mode: "string",
      withTimezone: true,
    }),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("order_code").on(t.code),
    index("order_customer_id").on(t.customerId),
    index("order_status").on(t.status),
    index("order_channel").on(t.channel),
    index("order_social_sales_source_id_idx").on(t.socialSalesSourceId),
    index("order_shipping_carrier_id").on(t.shippingCarrierId),
    index("order_hal_pre_order_id").on(t.halPreOrderId),
    index("order_hal_shipment_number").on(t.halShipmentNumber),
    index("order_created_at").on(t.createdAt),
  ],
);

export const orderItem = pgTable(
  "order_item",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => order.id, { onDelete: "cascade" }),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariant.id, { onDelete: "restrict" }),
    quantity: integer("quantity").notNull(),
    price: integer("price").notNull(),
    total: integer("total").notNull(),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("order_item_order_id").on(t.orderId),
    index("order_item_variant_id").on(t.variantId),
  ],
);

/** Order status history (log) */
export const orderStatusHistory = pgTable(
  "order_status_history",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => order.id, { onDelete: "cascade" }),
    status: text("status", { enum: orderStatusEnum }).notNull(),
    note: text("note"),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("order_status_history_order_id").on(t.orderId),
    index("order_status_history_created_at").on(t.createdAt),
  ],
);

export const orderAttachmentKindEnum = [
  "packing",
  "shipping_document",
  "payment_slip",
] as const;

/** รูปแพ็ก / บิลขนส่ง แนบต่อ order */
export const orderAttachment = pgTable(
  "order_attachment",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => order.id, { onDelete: "cascade" }),
    kind: text("kind", { enum: orderAttachmentKindEnum }).notNull(),
    fileKey: text("file_key").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("order_attachment_order_id").on(t.orderId),
    index("order_attachment_order_id_kind").on(t.orderId, t.kind),
  ],
);
